import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Typography,
  Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SelectChangeEvent } from '@mui/material/Select';
import CommonPagination from '../../common/Pagination';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { InventoryRecordsService } from '../../../api/services/InventoryRecordsService';
import type { InventoryRecordItemResponseDto } from '../../../api/models/InventoryRecordItemResponseDto';
import { printInventoryLabels } from '../../../utils/printInventoryLabels';
import { InventoryLabelPreviewMini } from '../InventoryLabelPreviewMini';
import * as S from './InventoryLabelPrintDrawer.styles';

const LABEL_PRINT_ITEMS_PER_PAGE_OPTIONS = [5, 10, 15, 20, 25];
/** Siempre al abrir / recargar el flujo de impresión de un registro. */
const LABEL_PRINT_DEFAULT_PAGE_SIZE = 10;

const DRAWER_ALERT_BASE_SX = {
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box' as const,
  bgcolor: 'transparent',
  '& .MuiAlert-message': { overflowWrap: 'break-word' as const },
};

function drawerAlertWarningSx(theme: Theme) {
  return {
    ...DRAWER_ALERT_BASE_SX,
    color: theme.palette.mode === 'dark' ? theme.palette.warning.light : '#B45309',
    borderColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.55 : 0.65),
    '& .MuiAlert-icon': { color: theme.palette.warning.main },
  };
}

function drawerAlertInfoSx(theme: Theme) {
  return {
    ...DRAWER_ALERT_BASE_SX,
    color: theme.palette.text.primary,
    borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.5 : 0.45),
    '& .MuiAlert-icon': { color: theme.palette.info.light },
  };
}

function drawerAlertErrorSx(theme: Theme) {
  return {
    ...DRAWER_ALERT_BASE_SX,
    color: theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.dark,
    borderColor: alpha(theme.palette.error.main, 0.45),
    '& .MuiAlert-icon': { color: theme.palette.error.light },
  };
}

export interface LabelPrintItem {
  warehouseInventoryProductId: number;
  inventoryRecordItemId: number | null;
  catalogProductCode: string;
  catalogProductId: number | null;
  productCodeUnique: string;
  productType: string;
  materialLabel: string;
  salePrice: number;
  weightGrams: number | null;
  diameterSize: string | null;
  workshopCode: string | null;
}

export interface InventoryLabelPrintModalProps {
  open: boolean;
  onClose: () => void;
  recordId: number | null;
  recordCode?: string;
}

const TOTAL_STEPS = 3;

const STEP_TITLES = ['Selección de productos', 'Vista previa e impresión por lote', 'Confirmación'];

function normCode(s: string | undefined | null): string {
  return (s ?? '').trim().toLowerCase();
}

function normalizeLineId(id: number | string | undefined): number {
  const n = Number(id);
  return Number.isFinite(n) ? n : NaN;
}

function pickInventoryRecordItemId(row: {
  inventoryRecordItemId?: number | null | string;
  inventory_record_item_id?: number | null | string;
}): number | null {
  const v = row.inventoryRecordItemId ?? row.inventory_record_item_id;
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pickCatalogProductId(row: {
  catalogProductId?: number | null | string;
  catalog_product_id?: number | null | string;
}): number | null {
  const v = row.catalogProductId ?? row.catalog_product_id;
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function labelBelongsToLine(label: LabelPrintItem, line: InventoryRecordItemResponseDto): boolean {
  const lineId = normalizeLineId(line.id);
  if (Number.isNaN(lineId)) return false;

  if (label.inventoryRecordItemId != null) {
    return label.inventoryRecordItemId === lineId;
  }

  const lp = normCode(label.catalogProductCode);
  const lc = normCode(line.product?.code);
  if (lp !== '' && lc !== '' && lp === lc) return true;

  const catId = label.catalogProductId;
  if (catId != null && Number(catId) === Number(line.productId)) return true;
  if (catId != null && line.product?.id != null && Number(catId) === Number(line.product.id)) {
    return true;
  }

  return false;
}

const InventoryLabelPrintModal: React.FC<InventoryLabelPrintModalProps> = ({
  open,
  onClose,
  recordId,
  recordCode: recordCodeProp,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<LabelPrintItem[]>([]);
  const [recordLines, setRecordLines] = useState<InventoryRecordItemResponseDto[]>([]);
  const [recordCode, setRecordCode] = useState('');
  const [selectedLineIds, setSelectedLineIds] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(LABEL_PRINT_DEFAULT_PAGE_SIZE);
  const [printing, setPrinting] = useState(false);
  const [printFeedback, setPrintFeedback] = useState<string | null>(null);

  const allLineIds = useMemo(
    () => recordLines.map((l) => normalizeLineId(l.id)).filter((id) => !Number.isNaN(id)),
    [recordLines],
  );

  useEffect(() => {
    if (!open) {
      setStep(1);
      setPrintFeedback(null);
      setPrinting(false);
    }
  }, [open]);

  const load = useCallback(async () => {
    if (recordId == null) return;
    setLoading(true);
    setError(null);
    setStep(1);
    try {
      const [labelRes, detailRes] = await Promise.all([
        InventoryRecordsService.inventoryRecordsControllerFindLabelPrintItems(Number(recordId)),
        InventoryRecordsService.inventoryRecordsControllerFindOneDetailed(String(recordId), false),
      ]);

      const rcRaw = labelRes.recordCode ?? detailRes.recordCode ?? recordCodeProp ?? '';
      const rc = typeof rcRaw === 'string' ? rcRaw : '';
      setRecordCode(rc);

      const list = Array.isArray(labelRes.items) ? labelRes.items : [];
      setItems(
        list.map((row) => {
          const r = row as {
            warehouseInventoryProductId: number;
            catalogProductCode?: string;
            catalog_product_code?: string;
            productCodeUnique: string;
            productType: string;
            materialLabel: string;
            salePrice: number;
            weightGrams: number | null;
            diameterSize?: string | null;
            workshopCode?: string | null;
          };
          return {
            warehouseInventoryProductId: r.warehouseInventoryProductId,
            inventoryRecordItemId: pickInventoryRecordItemId(row as never),
            catalogProductCode: r.catalogProductCode ?? r.catalog_product_code ?? '',
            catalogProductId: pickCatalogProductId(row as never),
            productCodeUnique: r.productCodeUnique,
            productType: r.productType,
            materialLabel: r.materialLabel,
            salePrice: r.salePrice,
            weightGrams: r.weightGrams,
            diameterSize: (r.diameterSize ?? '').trim() || null,
            workshopCode: (r.workshopCode ?? '').trim() || null,
          };
        }),
      );

      const lines = detailRes.items ?? [];
      setRecordLines(lines);
      setSelectedLineIds(
        new Set(lines.map((l) => normalizeLineId(l.id)).filter((id) => !Number.isNaN(id))),
      );
      setPage(1);
      setPageSize(LABEL_PRINT_DEFAULT_PAGE_SIZE);
    } catch (e: unknown) {
      console.error(e);
      setError('No se pudieron cargar los datos para etiquetas.');
      setItems([]);
      setRecordLines([]);
      setSelectedLineIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [recordId, recordCodeProp]);

  useEffect(() => {
    if (open && recordId != null) {
      load();
    }
  }, [open, recordId, load]);

  const lineLabelCounts = useMemo(() => {
    const m = new Map<number, number>();
    for (const line of recordLines) {
      const lid = normalizeLineId(line.id);
      if (Number.isNaN(lid)) continue;
      const n = items.filter((lbl) => labelBelongsToLine(lbl, line)).length;
      m.set(lid, n);
    }
    return m;
  }, [items, recordLines]);

  const filteredItems = useMemo(() => {
    if (selectedLineIds.size === 0) return [];
    return items.filter((lbl) =>
      recordLines.some((line) => {
        const lid = normalizeLineId(line.id);
        if (Number.isNaN(lid)) return false;
        return selectedLineIds.has(lid) && labelBelongsToLine(lbl, line);
      }),
    );
  }, [items, recordLines, selectedLineIds]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize) || 1);
  const safePage = Math.min(page, totalPages);
  const pageSlice = useMemo(() => {
    const p = Math.min(Math.max(1, page), totalPages);
    const start = (p - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, page, pageSize, totalPages]);

  const allSelected = allLineIds.length > 0 && allLineIds.every((id) => selectedLineIds.has(id));
  const someSelected = allLineIds.some((id) => selectedLineIds.has(id));

  const canProceedStep1 = filteredItems.length > 0;
  const canPrint = filteredItems.length > 0 && pageSlice.length > 0;

  const progressPercent = Math.round((step / TOTAL_STEPS) * 100);

  const toggleLine = (rawLineId: number | string) => {
    const lineId = normalizeLineId(rawLineId);
    if (Number.isNaN(lineId)) return;
    setSelectedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
    setPage(1);
  };

  const onSelectAllHeaderChange = () => {
    if (allSelected) {
      setSelectedLineIds(new Set());
    } else {
      setSelectedLineIds(new Set(allLineIds));
    }
    setPage(1);
  };

  const handleLabelItemsPerPageChange = (e: SelectChangeEvent<unknown>) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const handlePrintBatch = async () => {
    if (!canPrint || printing) return;
    setPrinting(true);
    setPrintFeedback(null);
    try {
      const result = await printInventoryLabels(
        pageSlice.map((r) => ({
          productCodeUnique: r.productCodeUnique,
          materialLabel: r.materialLabel,
          salePrice: r.salePrice,
          weightGrams: r.weightGrams,
          diameterSize: r.diameterSize,
          workshopCode: r.workshopCode,
        })),
      );
      if (result.ok) {
        if (result.channel === 'browser-print') {
          setPrintFeedback('Etiquetas enviadas (Zebra Browser Print).');
        } else if (result.channel === 'local-agent') {
          setPrintFeedback('Etiquetas enviadas (agente local CUPS).');
        } else {
          setPrintFeedback(
            'Diálogo de impresión abierto. En Chrome: quite "Encabezado y pie de página", márgenes Ninguno, papel 88×10 mm (o el tamaño de su rollo), impresora GC420t.',
          );
        }
      } else {
        setPrintFeedback(result.message);
      }
    } finally {
      setPrinting(false);
    }
  };

  const displayCode = recordCode || recordCodeProp || '';

  const renderStep1 = () => (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            bgcolor: 'action.hover',
            px: 3,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Productos ({recordLines.length})
          </Typography>
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={onSelectAllHeaderChange}
                size="small"
              />
            }
            label={<Typography variant="body2">Todos</Typography>}
          />
        </Box>

        {recordLines.length === 0 ? (
          <Box sx={{ px: 3, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No hay líneas de producto en este registro.
            </Typography>
          </Box>
        ) : (
          recordLines.map((line, idx) => {
            const lineId = normalizeLineId(line.id);
            const nLabels = Number.isNaN(lineId) ? 0 : (lineLabelCounts.get(lineId) ?? 0);
            return (
              <Box
                key={Number.isNaN(lineId) ? `line-${idx}` : lineId}
                sx={{
                  px: 3,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: idx < recordLines.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                  transition: 'background-color 0.2s',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Checkbox
                    checked={!Number.isNaN(lineId) && selectedLineIds.has(lineId)}
                    onChange={() => toggleLine(line.id)}
                    size="small"
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: 'text.primary', display: 'block' }}
                      noWrap
                    >
                      {line.product?.name || `Producto ${line.productId}`}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', display: 'block' }}
                    >
                      {line.product?.code || '-'}
                      {nLabels === 0 ? ' · sin etiquetas para esta línea' : ''}
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={`${line.quantity} pza.`}
                  size="small"
                  sx={{
                    bgcolor: 'action.selected',
                    color: 'text.primary',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    flexShrink: 0,
                  }}
                />
              </Box>
            );
          })
        )}
      </Box>
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          Etiquetas que se imprimirán con la selección actual:{' '}
          <strong>{filteredItems.length}</strong>
        </Typography>
      </Box>
      {selectedLineIds.size === 0 && recordLines.length > 0 && (
        <Alert
          severity="warning"
          variant="outlined"
          sx={(theme) => ({ ...drawerAlertWarningSx(theme), mt: 1.5 })}
        >
          Seleccione al menos una línea con etiquetas para continuar.
        </Alert>
      )}
      {!canProceedStep1 && selectedLineIds.size > 0 && recordLines.length > 0 && (
        <Alert
          severity="info"
          variant="outlined"
          sx={(theme) => ({ ...drawerAlertInfoSx(theme), mt: 1.5 })}
        >
          Ninguna etiqueta coincide con las líneas marcadas. Revise la selección.
        </Alert>
      )}
    </Box>
  );

  const renderStep2 = () => (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'stretch',
      }}
    >
      {filteredItems.length > 0 ? (
        <Box sx={{ mb: 1.5, flexShrink: 0 }}>
          <CommonPagination
            currentPage={safePage}
            itemsPerPage={pageSize}
            totalItems={filteredItems.length}
            onPageChange={setPage}
            onItemsPerPageChange={handleLabelItemsPerPageChange}
            itemsPerPageOptions={LABEL_PRINT_ITEMS_PER_PAGE_OPTIONS}
            itemLabelPlural="etiquetas"
          />
        </Box>
      ) : null}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          alignItems: 'flex-start',
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          p: 1.5,
          bgcolor: 'action.hover',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {pageSlice.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay etiquetas en este lote.
          </Typography>
        ) : (
          pageSlice.map((row) => (
            <Box
              key={row.warehouseInventoryProductId}
              sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}
            >
              <InventoryLabelPreviewMini row={row} />
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 220 }}>
                {row.productType}
              </Typography>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );

  const renderStep3 = () => (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        minHeight: 320,
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: 'action.hover',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Resumen antes de imprimir
        </Typography>
      </Box>
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2">
          <strong>Registro:</strong> {displayCode || '—'}
        </Typography>
        <Typography variant="body2">
          <strong>Total de etiquetas en la selección:</strong> {filteredItems.length}
        </Typography>
        <Typography variant="body2">
          <strong>Etiquetas por página (lote):</strong> {pageSize}
        </Typography>
        <Typography variant="body2">
          <strong>Lote a imprimir:</strong> página {safePage} de {totalPages}
        </Typography>
        <Typography variant="body2">
          <strong>En esta impresión:</strong> {pageSlice.length} etiqueta(s)
        </Typography>
        <Alert
          severity="info"
          variant="outlined"
          sx={(theme) => ({ ...drawerAlertInfoSx(theme), mt: 1 })}
        >
          Impresión: intenta envío silencioso; si no está disponible, abre el diálogo de Chrome. En
          Windows elija la GC420t (driver Zebra), papel personalizado <strong>88×10 mm</strong>,
          márgenes 0. No hace falta Browser Print en cada PC.
        </Alert>
        {printFeedback != null ? (
          <Alert
            severity={printFeedback.startsWith('Etiquetas enviadas') ? 'success' : 'warning'}
            variant="outlined"
            sx={(theme) =>
              printFeedback.startsWith('Etiquetas enviadas')
                ? drawerAlertInfoSx(theme)
                : drawerAlertWarningSx(theme)
            }
          >
            {printFeedback}
          </Alert>
        ) : null}
      </Box>
    </Box>
  );

  const renderMainContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            minHeight: 360,
          }}
        >
          <CircularProgress size={56} />
        </Box>
      );
    }
    if (error) {
      return (
        <Alert severity="error" variant="outlined" sx={(theme) => drawerAlertErrorSx(theme)}>
          {error}
        </Alert>
      );
    }
    if (items.length === 0) {
      return (
        <Alert severity="info" variant="outlined" sx={(theme) => drawerAlertInfoSx(theme)}>
          No hay piezas de almacén para imprimir en este registro (o el registro está cancelado).
        </Alert>
      );
    }

    switch (step) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return null;
    }
  };

  const handleFooterPrevious = () => {
    if (step === 1) {
      onClose();
    } else {
      setStep((s) => (s === 2 ? 1 : 2) as 1 | 2 | 3);
    }
  };

  const handleFooterNext = () => {
    if (step === 1 && canProceedStep1) setStep(2);
    else if (step === 2 && canPrint) setStep(3);
  };

  if (!open) {
    return null;
  }

  return (
    <S.StyledDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: S.labelPrintDrawerPaperSx }}
    >
      <S.DrawerContainer>
        <S.DrawerHeader>
          <S.DrawerTitle component="div">
            <ReceiptIcon sx={{ color: 'info.main' }} />
            Imprimir etiquetas
            {displayCode ? (
              <Box component="span" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                · {displayCode}
              </Box>
            ) : null}
          </S.DrawerTitle>
          <S.CloseButton onClick={onClose} aria-label="Cerrar">
            <CloseIcon />
          </S.CloseButton>
        </S.DrawerHeader>

        <S.ProgressContainer>
          <S.ProgressBar>
            <S.ProgressBarFill style={{ width: `${progressPercent}%` }} />
          </S.ProgressBar>
          <S.ProgressInfo>
            <span>
              Paso {step} de {TOTAL_STEPS} · {STEP_TITLES[step - 1]}
            </span>
            <span>{progressPercent}%</span>
          </S.ProgressInfo>
        </S.ProgressContainer>

        <S.ContentContainer>{renderMainContent()}</S.ContentContainer>

        <S.DrawerFooter>
          <S.PreviousButton onClick={handleFooterPrevious} disabled={loading}>
            {step === 1 ? 'Cancelar' : 'Anterior'}
          </S.PreviousButton>
          <S.FooterButtonsContainer>
            {!error && !loading && items.length > 0 ? (
              <>
                {step < 3 ? (
                  <S.NextButton
                    variant="contained"
                    onClick={handleFooterNext}
                    disabled={(step === 1 && !canProceedStep1) || (step === 2 && !canPrint)}
                  >
                    Siguiente
                  </S.NextButton>
                ) : (
                  <S.ConfirmButton
                    variant="contained"
                    startIcon={
                      printing ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />
                    }
                    onClick={() => {
                      void handlePrintBatch();
                    }}
                    disabled={!canPrint || printing}
                  >
                    {printing ? 'Imprimiendo…' : 'Imprimir etiquetas'}
                  </S.ConfirmButton>
                )}
              </>
            ) : (
              <Button
                variant="outlined"
                onClick={onClose}
                sx={(theme) => ({
                  textTransform: 'none',
                  borderRadius: '10px',
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  backgroundColor: theme.palette.background.paper,
                  '&:hover': {
                    borderColor: theme.palette.text.secondary,
                    backgroundColor: theme.palette.action.hover,
                  },
                })}
              >
                Cerrar
              </Button>
            )}
          </S.FooterButtonsContainer>
        </S.DrawerFooter>
      </S.DrawerContainer>
    </S.StyledDrawer>
  );
};

export default InventoryLabelPrintModal;
